import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Box, Chip, Stack, Typography } from '@mui/material'
import { SectionDefinition } from './Sections.types'
import SectionIcon from './SectionIcon.component'

// keeps a section's heading clear of the sticky nav when jumping to it
const NAV_OFFSET = 80

type SectionedFormContextValue = {
  getDomId: (sectionId: string) => string
  getSection: (sectionId: string) => SectionDefinition | undefined
  register: (sectionId: string) => () => void
}
const SectionedFormContext = createContext<SectionedFormContextValue | undefined>(undefined)

// forms are normally rendered inside a dialog, so the element that scrolls is an ancestor rather than the window
const findScrollParent = (element: HTMLElement | null): HTMLElement | null => {
  let node = element?.parentElement ?? null
  while (node) {
    const { overflowY } = window.getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node
    }
    node = node.parentElement
  }
  return null
}

type SectionedFormProps = {
  // every section the form may render, in display order. Only sections that are actually rendered
  // (e.g. the user has permission for them) show up in the nav.
  sections: SectionDefinition[]
  // section to scroll to once the form is shown
  initialSection?: string
  children: React.ReactNode
}
/**
 * @description Wraps a long form that is split into FormSections. Renders a sticky nav of the sections that
 * highlights where the user currently is and lets them jump straight to any section.
 */
const SectionedForm = (props: SectionedFormProps) => {
  const { sections, initialSection, children } = props
  const prefix = useId()
  const navRef = useRef<HTMLDivElement>(null)
  const chipRowRef = useRef<HTMLDivElement>(null)
  // set when the user clicks a section in the nav so the highlight doesn't bounce around during the smooth scroll
  const pinnedSectionRef = useRef<string | null>(null)
  const [registered, setRegistered] = useState<string[]>([])
  const [activeId, setActiveId] = useState<string>()
  // sticky positioning is measured from inside the scroll container's padding, so offset by it to sit flush at the top
  const [scrollerPadding, setScrollerPadding] = useState({ top: 0, left: 0, right: 0 })

  const getDomId = useCallback((sectionId: string) => `${prefix}-section-${sectionId}`, [prefix])
  const getSection = useCallback((sectionId: string) => sections.find((each) => each.id === sectionId), [sections])
  const register = useCallback((sectionId: string) => {
    setRegistered((prev) => (prev.includes(sectionId) ? prev : [...prev, sectionId]))
    return () => setRegistered((prev) => prev.filter((each) => each !== sectionId))
  }, [])
  const contextValue = useMemo(() => ({ getDomId, getSection, register }), [getDomId, getSection, register])

  const visibleSections = useMemo(
    () => sections.filter((section) => registered.includes(section.id)),
    [sections, registered],
  )

  const jumpTo = useCallback(
    (sectionId: string, behavior: ScrollBehavior = 'smooth') => {
      pinnedSectionRef.current = sectionId
      setActiveId(sectionId)
      document.getElementById(getDomId(sectionId))?.scrollIntoView({ behavior, block: 'start' })
    },
    [getDomId],
  )

  // track which section is currently at the top of the scroll area
  useEffect(() => {
    const nav = navRef.current
    if (!nav || !visibleSections.length) return
    const scroller = findScrollParent(nav)
    const target: HTMLElement | Window = scroller ?? window
    if (scroller) {
      const style = window.getComputedStyle(scroller)
      setScrollerPadding({
        top: parseFloat(style.paddingTop) || 0,
        left: parseFloat(style.paddingLeft) || 0,
        right: parseFloat(style.paddingRight) || 0,
      })
    }

    const update = () => {
      if (pinnedSectionRef.current) {
        setActiveId(pinnedSectionRef.current)
        return
      }
      const threshold = (scroller ? scroller.getBoundingClientRect().top : 0) + nav.offsetHeight + 24
      let current = visibleSections[0].id
      visibleSections.forEach((section) => {
        const element = document.getElementById(getDomId(section.id))
        if (element && element.getBoundingClientRect().top <= threshold) {
          current = section.id
        }
      })
      // the last section may be too short to ever reach the top, so treat being scrolled to the bottom as being on it
      const scrollTop = scroller ? scroller.scrollTop : window.scrollY
      const atBottom = scroller
        ? scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2
        : window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2
      if (scrollTop > 0 && atBottom) {
        current = visibleSections[visibleSections.length - 1].id
      }
      setActiveId(current)
    }
    // any manual scrolling releases a section pinned by clicking the nav
    const unpin = () => {
      pinnedSectionRef.current = null
    }

    update()
    target.addEventListener('scroll', update, { passive: true })
    target.addEventListener('wheel', unpin, { passive: true })
    target.addEventListener('touchstart', unpin, { passive: true })
    target.addEventListener('keydown', unpin)
    return () => {
      target.removeEventListener('scroll', update)
      target.removeEventListener('wheel', unpin)
      target.removeEventListener('touchstart', unpin)
      target.removeEventListener('keydown', unpin)
    }
  }, [visibleSections, getDomId])

  // keep the active chip visible when the nav overflows horizontally (narrow screens)
  useEffect(() => {
    const row = chipRowRef.current
    const chip = row?.querySelector<HTMLElement>(`[data-section-chip="${activeId}"]`)
    if (!row || !chip) return
    if (chip.offsetLeft < row.scrollLeft || chip.offsetLeft + chip.offsetWidth > row.scrollLeft + row.clientWidth) {
      row.scrollTo({ left: chip.offsetLeft - 16, behavior: 'smooth' })
    }
  }, [activeId])

  // jump to the requested section once it has rendered
  const didInitialJumpRef = useRef(false)
  useEffect(() => {
    if (didInitialJumpRef.current || !initialSection || !registered.includes(initialSection)) return
    // wait for the dialog to finish laying out before jumping
    const handle = window.setTimeout(() => {
      didInitialJumpRef.current = true
      jumpTo(initialSection, 'auto')
    }, 50)
    return () => window.clearTimeout(handle)
  }, [initialSection, registered, jumpTo])

  return (
    <SectionedFormContext.Provider value={contextValue}>
      {visibleSections.length > 1 && (
        <Box
          ref={navRef}
          component="nav"
          aria-label="Form sections"
          sx={{
            position: 'sticky',
            top: -scrollerPadding.top,
            mt: `${-scrollerPadding.top}px`,
            zIndex: 2,
            ml: `${-scrollerPadding.left}px`,
            mr: `${-scrollerPadding.right}px`,
            pl: `${scrollerPadding.left}px`,
            pr: `${scrollerPadding.right}px`,
            py: 1.5,
            bgcolor: 'rgba(26, 26, 26, 0.94)',
            backdropFilter: 'blur(6px)',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Stack
            ref={chipRowRef}
            direction="row"
            spacing={1}
            sx={{
              position: 'relative',
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {visibleSections.map((section, index) => {
              const Icon = section.icon
              const isActive = section.id === activeId
              return (
                <Chip
                  key={section.id}
                  data-section-chip={section.id}
                  icon={<Icon fontSize="small" />}
                  label={`${index + 1}. ${section.title}`}
                  color={isActive ? 'primary' : 'default'}
                  variant={isActive ? 'filled' : 'outlined'}
                  aria-current={isActive ? 'step' : undefined}
                  onClick={() => jumpTo(section.id)}
                  sx={{ flexShrink: 0, fontWeight: isActive ? 700 : 400 }}
                />
              )
            })}
          </Stack>
        </Box>
      )}
      <Stack spacing={4} sx={{ my: 2 }}>
        {children}
      </Stack>
    </SectionedFormContext.Provider>
  )
}
export default SectionedForm

type FormSectionProps = {
  // id of a section passed to the parent SectionedForm
  id: string
  children: React.ReactNode
}
/**
 * @description One titled group of inputs inside a SectionedForm.
 */
export const FormSection = (props: FormSectionProps) => {
  const { id, children } = props
  const context = useContext(SectionedFormContext)
  const section = context?.getSection(id)

  useEffect(() => context?.register(id), [context, id])

  if (!context || !section) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`FormSection "${id}" must be rendered inside a SectionedForm that defines it`)
    }
    return null
  }

  return (
    <Box component="section" id={context.getDomId(id)} aria-label={section.title} sx={{ scrollMarginTop: NAV_OFFSET }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
        <SectionIcon icon={section.icon} />
        <Box>
          <Typography variant="h6" component="h3" sx={{ lineHeight: 1.3 }}>
            {section.title}
          </Typography>
          {section.description && (
            <Typography variant="body2" color="grey.400">
              {section.description}
            </Typography>
          )}
        </Box>
      </Stack>
      <Box
        sx={{
          p: { zero: 1.5, sm: 2.5 },
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          // a little lighter than the dialog behind it
          bgcolor: 'surface.section',
        }}
      >
        <Stack spacing={2.5}>{children}</Stack>
      </Box>
    </Box>
  )
}
